import { useEffect, useState, useRef, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import { getCategories } from "@/api/productApi"
import { getAdminProductDetail, updateAdminProduct } from "@/api/adminProductApi"
import { getS3PresignedUrl } from "@/api/s3Api"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus, X } from "lucide-react"
import ReactQuill from "react-quill-new"
import "react-quill-new/dist/quill.snow.css"

type Category = { categoryId: number; name: string }

// 💡 새 이미지(File)와 기존 이미지(URL)를 묶어서 관리하는 타입
type DetailImage = {
    id: string
    file?: File
    existingUrl?: string
    preview: string
}

// 💡 1. 타입 정의 추가
type OptionCombination = {
    optionId?: number;       // 백엔드에서 내려온 기존 ID
    name: string;
    additionalPrice: number;
    stockQuantity: number;
    isUse: boolean;
    isDeleted?: boolean;     // 💡 삭제 플래그
}

export default function AdminProductEditPage() {
    const navigate = useNavigate()
    const { id } = useParams()

    const [categories, setCategories] = useState<Category[]>([])
    const [loading, setLoading] = useState(false)
    const [loadingText, setLoadingText] = useState("상품 수정")

    // 대표 이미지 상태
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
    const [thumbnailPreview, setThumbnailPreview] = useState<string>("")

    // 상세 이미지 상태
    const [detailImages, setDetailImages] = useState<DetailImage[]>([])

    // 💡 2. 옵션 관련 State
    const [hasOptions, setHasOptions] = useState(false)
    const [optionCombinations, setOptionCombinations] = useState<OptionCombination[]>([])

    // 상세 설명 에디터 전용 핸들러
    const handleDescriptionChange = (content: string) => {
        setForm((prev) => ({ ...prev, description: content }))
    }

    // 에디터 DOM에 접근하기 위한 Ref
    const quillRef = useRef<any>(null)

    // S3 이미지 업로드 커스텀 핸들러 (에디터용)
    const imageHandler = () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();

        input.onchange = async () => {
            const file = input.files ? input.files[0] : null;
            if (!file) return;

            try {
                const { presignedUrl, imageUrl } = await getS3PresignedUrl(file.name);
                await fetch(presignedUrl, {
                    method: "PUT",
                    body: file,
                    headers: { "Content-Type": file.type },
                });

                const editor = quillRef.current?.getEditor();
                if (!editor) return;

                const range = editor.getSelection();
                const position = range ? range.index : editor.getLength();

                editor.insertEmbed(position, "image", imageUrl);
                editor.setSelection(position + 1, 0);

            } catch (error) {
                console.error("에디터 이미지 업로드 에러:", error);
                alert("이미지 업로드에 실패했습니다.");
            }
        };
    };

    // 툴바 모듈 설정
    const quillModules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ color: [] }, { background: [] }],
                ["link", "image", "clean"],
            ],
            handlers: {
                image: imageHandler,
            },
        },
    }), []);

    const [form, setForm] = useState({
        categoryId: "",
        productName: "",
        productCode: "",
        brandName: "",
        summary: "",
        description: "",
        price: "",
        discountPrice: "",
        stockQuantity: "",
        status: "SELLING",
        visibleYn: "Y",
    })

    useEffect(() => {
        const initData = async () => {
            if (!id) return;
            try {
                const [catRes, prodRes] = await Promise.all([
                    getCategories(),
                    getAdminProductDetail(id)
                ]);

                // 1. 카테고리 세팅
                if (catRes.success || catRes.data) setCategories(catRes.data.items || catRes.data);

                // 2. 기존 상품 정보 세팅
                const product = prodRes.data || prodRes;
                setForm({
                    categoryId: product.categoryId.toString(),
                    productName: product.productName,
                    productCode: product.productCode || "",
                    brandName: product.brandName || "",
                    summary: product.summary || "",
                    description: product.description || "",
                    price: product.price.toString(),
                    discountPrice: product.discountPrice ? product.discountPrice.toString() : "",
                    stockQuantity: product.stockQuantity.toString(),
                    status: product.status,
                    visibleYn: product.visibleYn,
                });

                // 3. 기존 대표 이미지 세팅
                setThumbnailPreview(product.thumbnailUrl);

                // 4. 기존 상세 이미지 세팅
                if (product.imageUrls && product.imageUrls.length > 0) {
                    const existingDetails = product.imageUrls.map((url: string, index: number) => ({
                        id: `old-${index}`,
                        existingUrl: url,
                        preview: url
                    }));
                    setDetailImages(existingDetails);
                }

                // 💡 5. 백엔드에서 받아온 기존 옵션 데이터 세팅
                if (product.options && product.options.length > 0) {
                    setHasOptions(true);
                    setOptionCombinations(product.options.map((opt: any) => ({
                        optionId: opt.optionId,
                        name: opt.name,
                        additionalPrice: opt.additionalPrice,
                        stockQuantity: opt.stockQuantity,
                        isUse: opt.isUse,
                        isDeleted: false
                    })));
                } else {
                    setHasOptions(false);
                }

            } catch (error) {
                console.error("초기 데이터 로딩 실패:", error);
                alert("데이터를 불러오지 못했습니다.");
                navigate("/admin/products");
            }
        };
        initData();
    }, [id, navigate]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setForm((prev) => ({ ...prev, [name]: value }))
    }

    // 대표 이미지 변경
    const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setThumbnailFile(file)
            setThumbnailPreview(URL.createObjectURL(file))
        }
    }

    // 상세 이미지 추가
    const handleDetailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || [])
        const newImages = files.map(file => ({
            id: `new-${Date.now()}-${file.name}`,
            file: file,
            preview: URL.createObjectURL(file)
        }))
        setDetailImages(prev => [...prev, ...newImages])
    }

    // 상세 이미지 삭제
    const removeDetail = (idToRemove: string) => {
        setDetailImages(prev => prev.filter(img => img.id !== idToRemove))
    }

    // S3 업로드 유틸
    const uploadToS3 = async (file: File): Promise<string> => {
        const { presignedUrl, imageUrl } = await getS3PresignedUrl(file.name)
        await fetch(presignedUrl, {
            method: "PUT",
            body: file,
            headers: { "Content-Type": file.type },
        })
        return imageUrl
    }

    // 💡 6. 옵션 데이터 핸들러
    const handleCombinationChange = (index: number, field: keyof OptionCombination, value: any) => {
        const newCombs = [...optionCombinations]
        newCombs[index] = { ...newCombs[index], [field]: value }
        setOptionCombinations(newCombs)
    }

    // 💡 7. 옵션 삭제 시 배열에서 빼지 않고 isDeleted 플래그만 true로 변경
    const handleRemoveOption = (index: number) => {
        const newCombs = [...optionCombinations]
        newCombs[index].isDeleted = true
        setOptionCombinations(newCombs)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!form.categoryId) return alert("카테고리를 선택해주세요.")
        if (!thumbnailPreview) return alert("대표 이미지를 등록해주세요.")

        try {
            setLoading(true)
            setLoadingText("이미지 처리 중...")

            let finalThumbnailUrl = thumbnailPreview;
            if (thumbnailFile) {
                finalThumbnailUrl = await uploadToS3(thumbnailFile);
            }

            const finalDetailUrls: string[] = [];
            for (const img of detailImages) {
                if (img.file) {
                    const uploadedUrl = await uploadToS3(img.file);
                    finalDetailUrls.push(uploadedUrl);
                } else if (img.existingUrl) {
                    finalDetailUrls.push(img.existingUrl);
                }
            }

            setLoadingText("상품 정보 수정 중...")

            // 💡 8. 최종 Payload 구성
            const payload = {
                categoryId: Number(form.categoryId),
                productName: form.productName,
                productCode: form.productCode || undefined,
                brandName: form.brandName || undefined,
                summary: form.summary || undefined,
                description: form.description || undefined,
                price: Number(form.price),
                discountPrice: form.discountPrice ? Number(form.discountPrice) : undefined,
                stockQuantity: Number(form.stockQuantity),
                status: form.status,
                visibleYn: form.visibleYn,
                thumbnailUrl: finalThumbnailUrl,
                imageUrls: finalDetailUrls,

                // 옵션 데이터 통신 포함
                hasOptions: hasOptions,
                options: hasOptions ? optionCombinations : []
            }

            const response = await updateAdminProduct(id!, payload)
            if (response) {
                alert("상품 정보가 수정되었습니다.")
                navigate(`/admin/products/${id}`)
            }
        } catch (error) {
            console.error(error)
            alert("상품 수정 중 오류가 발생했습니다.")
        } finally {
            setLoading(false)
            setLoadingText("상품 수정")
        }
    }

    return (
      <AdminLayout>
          <div className="space-y-6">
              <div>
                  <h2 className="text-3xl font-bold">상품 수정</h2>
                  <p className="mt-2 text-sm text-slate-500">등록된 상품 정보를 수정합니다.</p>
              </div>

              <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                  <CardHeader>
                      <CardTitle className="text-xl">상품 기본 정보</CardTitle>
                  </CardHeader>
                  <CardContent>
                      <form onSubmit={handleSubmit} className="grid gap-5 md:grid-cols-2">
                          {/* --- 기존 텍스트 입력 영역 --- */}
                          <div className="space-y-2">
                              <Label>카테고리</Label>
                              <select name="categoryId" value={form.categoryId} onChange={handleChange} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none">
                                  <option value="">카테고리 선택</option>
                                  {categories.map((c) => <option key={c.categoryId} value={c.categoryId}>{c.name}</option>)}
                              </select>
                          </div>
                          <div className="space-y-2">
                              <Label>상품명</Label>
                              <Input name="productName" value={form.productName} onChange={handleChange} className="h-12 rounded-xl" required />
                          </div>
                          <div className="space-y-2">
                              <Label>상품코드</Label>
                              <Input name="productCode" value={form.productCode} onChange={handleChange} className="h-12 rounded-xl" />
                          </div>
                          <div className="space-y-2">
                              <Label>브랜드명</Label>
                              <Input name="brandName" value={form.brandName} onChange={handleChange} className="h-12 rounded-xl" />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                              <Label>상품 요약</Label>
                              <Input name="summary" value={form.summary} onChange={handleChange} className="h-12 rounded-xl" />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                              <Label>상품 상세 설명</Label>
                              <div className="rounded-xl border-slate-200 bg-white">
                                  <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    modules={quillModules}
                                    value={form.description}
                                    onChange={handleDescriptionChange}
                                    placeholder="상품 상세 설명을 자유롭게 작성해주세요."
                                    style={{ height: "300px", marginBottom: "40px" }} // 에디터 높이 확보
                                  />
                              </div>
                          </div>
                          <div className="space-y-2">
                              <Label>판매가</Label>
                              <Input type="number" name="price" value={form.price} onChange={handleChange} className="h-12 rounded-xl" required />
                          </div>
                          <div className="space-y-2">
                              <Label>할인가</Label>
                              <Input type="number" name="discountPrice" value={form.discountPrice} onChange={handleChange} className="h-12 rounded-xl" />
                          </div>
                          <div className="space-y-2">
                              <Label>재고수량</Label>
                              <Input type="number" name="stockQuantity" value={form.stockQuantity} onChange={handleChange} className="h-12 rounded-xl" required />
                          </div>
                          <div className="space-y-2">
                              <Label>상태</Label>
                              <select name="status" value={form.status} onChange={handleChange} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none">
                                  <option value="SELLING">판매중</option>
                                  <option value="SOLD_OUT">품절</option>
                                  <option value="HIDDEN">숨김</option>
                              </select>
                          </div>
                          <div className="space-y-2">
                              <Label>노출여부</Label>
                              <select name="visibleYn" value={form.visibleYn} onChange={handleChange} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none">
                                  <option value="Y">노출</option>
                                  <option value="N">비노출</option>
                              </select>
                          </div>

                          {/* ========================================== */}
                          {/* 💡 옵션 설정 영역 (수정 전용) */}
                          {/* ========================================== */}
                          <div className="md:col-span-2 border border-slate-200 rounded-2xl shadow-sm overflow-hidden mt-4 mb-2">
                              <div className="bg-slate-50 border-b border-slate-100 flex flex-row items-center justify-between p-5">
                                  <div>
                                      <h3 className="text-lg font-bold text-slate-800">상품 옵션 설정</h3>
                                      <p className="text-xs text-slate-500 mt-1">기존 옵션의 가격, 재고를 수정하거나 삭제할 수 있습니다.</p>
                                  </div>
                                  <label className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2 border border-slate-200 rounded-xl shadow-sm">
                                      <input type="checkbox" checked={hasOptions} onChange={(e) => setHasOptions(e.target.checked)} className="w-4 h-4 text-indigo-600 rounded" />
                                      <span className="font-bold text-sm text-slate-700">옵션 사용</span>
                                  </label>
                              </div>

                              {hasOptions && (
                                <div className="p-6 bg-white">
                                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-slate-100 text-slate-600 border-b border-slate-200">
                                            <tr>
                                                <th className="p-3 font-bold w-1/3">옵션명</th>
                                                <th className="p-3 font-bold w-1/4">추가 금액(원)</th>
                                                <th className="p-3 font-bold w-1/4">재고 수량(개)</th>
                                                <th className="p-3 font-bold text-center w-24">사용 여부</th>
                                                <th className="p-3 font-bold text-center w-20">삭제</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {optionCombinations.map((comb, idx) => {
                                                // 🚨 [핵심] 삭제 플래그(isDeleted)가 true인 항목은 화면에 그리지 않고 숨깁니다!
                                                if (comb.isDeleted) return null;

                                                return (
                                                  <tr key={idx} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                                                      <td className="p-3 font-medium text-slate-700">
                                                          {comb.optionId ? (
                                                            <span>{comb.name}</span>
                                                          ) : (
                                                            <Input
                                                              value={comb.name}
                                                              onChange={e => handleCombinationChange(idx, 'name', e.target.value)}
                                                              placeholder="옵션명 입력"
                                                              className="h-9"
                                                            />
                                                          )}
                                                      </td>
                                                      <td className="p-3">
                                                          <Input type="number" value={comb.additionalPrice} onChange={e => handleCombinationChange(idx, 'additionalPrice', Number(e.target.value))} className="h-9" />
                                                      </td>
                                                      <td className="p-3">
                                                          <Input type="number" value={comb.stockQuantity} onChange={e => handleCombinationChange(idx, 'stockQuantity', Number(e.target.value))} className="h-9" />
                                                      </td>
                                                      <td className="p-3 text-center">
                                                          <input type="checkbox" checked={comb.isUse} onChange={e => handleCombinationChange(idx, 'isUse', e.target.checked)} className="w-4 h-4 text-indigo-600 rounded cursor-pointer" />
                                                      </td>
                                                      <td className="p-3 text-center">
                                                          <Button type="button" variant="ghost" onClick={() => handleRemoveOption(idx)} className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-2 h-auto">
                                                              <X size={18} />
                                                          </Button>
                                                      </td>
                                                  </tr>
                                                )
                                            })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="mt-4 flex justify-end">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          onClick={() => {
                                              setOptionCombinations([...optionCombinations, { name: "", additionalPrice: 0, stockQuantity: 999, isUse: true, isDeleted: false }]);
                                          }}
                                          className="rounded-xl text-sm border-dashed text-slate-600 border-slate-300 bg-white"
                                        >
                                            <Plus size={16} className="mr-1"/> 수동 옵션 추가
                                        </Button>
                                    </div>
                                </div>
                              )}
                          </div>

                          <hr className="md:col-span-2 my-4 border-slate-200" />

                          {/* --- 💡 이미지 영역 --- */}
                          <div className="space-y-3 md:col-span-2">
                              <Label className="text-base font-semibold">대표 이미지 (1장 필수)</Label>
                              <div>
                                  {thumbnailPreview ? (
                                    <div className="relative w-36 h-36 border border-slate-200 rounded-xl overflow-hidden group">
                                        <img src={thumbnailPreview} alt="대표" className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <button type="button" onClick={() => { setThumbnailPreview(""); setThumbnailFile(null) }} className="bg-white text-red-500 rounded-full p-2">
                                                <X size={20} />
                                            </button>
                                        </div>
                                    </div>
                                  ) : (
                                    <label className="flex flex-col items-center justify-center w-36 h-36 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50">
                                        <Plus className="text-slate-400 mb-2" size={28} />
                                        <span className="text-sm text-slate-500 font-medium">이미지 등록</span>
                                        <input type="file" accept="image/*" className="hidden" onChange={handleThumbnailChange} />
                                    </label>
                                  )}
                              </div>
                          </div>

                          <div className="space-y-3 md:col-span-2">
                              <Label className="text-base font-semibold">상세 이미지</Label>
                              <div className="flex flex-wrap gap-4">
                                  {detailImages.map((img, index) => (
                                    <div key={img.id} className="relative w-36 h-36 border border-slate-200 rounded-xl overflow-hidden group">
                                        <img src={img.preview} alt={`상세-${index}`} className="w-full h-full object-cover" />
                                        <span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-md">{index + 1}</span>
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <button type="button" onClick={() => removeDetail(img.id)} className="bg-white text-red-500 rounded-full p-2">
                                                <X size={20} />
                                            </button>
                                        </div>
                                    </div>
                                  ))}
                                  <label className="flex flex-col items-center justify-center w-36 h-36 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50">
                                      <Plus className="text-slate-400 mb-2" size={28} />
                                      <span className="text-sm text-slate-500 font-medium">추가 등록</span>
                                      <input type="file" accept="image/*" multiple className="hidden" onChange={handleDetailChange} />
                                  </label>
                              </div>
                          </div>

                          <div className="md:col-span-2 flex justify-end gap-3 mt-8">
                              <Button type="button" variant="outline" className="rounded-xl h-12 px-6" onClick={() => navigate(`/admin/products/${id}`)} disabled={loading}>
                                  취소
                              </Button>
                              <Button type="submit" disabled={loading} className="rounded-xl h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white text-base font-medium transition-all">
                                  {loadingText}
                              </Button>
                          </div>
                      </form>
                  </CardContent>
              </Card>
          </div>
      </AdminLayout>
    )
}